

    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import * as toastr from "toastr";
    import Store from "@/store/store";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { appEventBus } from "@/utilities/EventBus";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import WorkPermitAddDialogue from "@/vue/components/WorkPermits/WorkPermitAddDialogue.vue";
    import { ISiteLookup } from "@/model/Site";
    import { ILookupItem, LookupItem } from "@/model/LookupItem";
    import { IUserLookup } from "@/model/UserLookup";
    import { IWorkPermit, WorkPermit } from "@/model/workPermits/WorkPermit";
    import { WorkPermitPerson } from "@/model/workPermits/WorkPermitPerson";
    import { WorkPermitSearchParameters } from "@/model/workPermits/WorkPermitSearchParameters";
    import { UserRole, WorkPermitFormType, WorkPermitEventType } from "@/model/Enums";
    import { IClientLookup } from "@/model/Client";

    interface ISearchResponse {
        list: Array<IWorkPermit>;
        count: number;
    }

    @Component({
        components: { ApiButton, WorkPermitAddDialogue }
    })
    export default class WorkPermits extends Vue {

        async mounted(): Promise<void> {
            appEventBus.on("deleted-work-permit", this.onWorkPermitDelete);
            await Promise.all([
                (async () => { this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups")); })(),
                (async () => { this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups")); })(),
                (async () => { this.userList = await ApiClient.get("/api/user/lookups"); })()
            ]);
        }

        beforeDestroy(): void {
            console.log("@@@ WP UN-mounted");
            appEventBus.off("deleted-work-permit", this.onWorkPermitDelete);
        }

        //
        // -- properties
        //

        workPermitFormType = WorkPermitFormType; // enum on form
        infiniteId: number = + new Date();
        searchParameters = new WorkPermitSearchParameters();

        userList: Array<IUserLookup> = [];

        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }

        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }

        get statusOptions(): Array<LookupItem> {
            const lookups = Utils.enumToLookups(WorkPermitEventType);
            lookups[0].description = "Any Status";
            return lookups;
        }

        resultCount: string = "-";
        workPermitList: Array<WorkPermit> = [];

        get isAdmin(): boolean {
            return Store.state.signedInUser?.role == UserRole.Admin;
        }

        //
        // -- Watchers
        //

        debouncedRefreshSearch = Utils.debounce(this.refreshSearch, 200);

        @Watch("searchParameters.clientID")
        onClientIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.siteID")
        onSiteIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.status")
        onStatusChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.searchText")
        onSearchTextChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.includeExpired")
        onIncludeExpiredChanged(): void {
            this.debouncedRefreshSearch();
        }

        //
        // -- methods
        //

        // we might be comparing a person object with a history event
        isSamePerson(a: any, b: any): boolean {
            if(a == null || b == null) return false;
            const aName = typeof a?.who === "undefined" ? a.name : a.who;
            const aEmail = typeof a?.whoID === "undefined" ? a.email : a.whoID;
            const bName = typeof b?.who === "undefined" ? b.name : b.who;
            const bEmail = typeof b?.whoID === "undefined" ? b.email : b.whoID;
            return aEmail == bEmail && aName == bName;
        }

        personBorderStyle(person: WorkPermitPerson): any {
            return {
                border: person.isMain ? "1px solid #888" : "1px solid #CCC",
                borderRadius: "6px"
            };
        }

        personHeaderStyle(workPermit: WorkPermit, person: WorkPermitPerson): any {
            const styles: any = {
                backgroundColor: person.isMain ? "#888" : "#CCC",
                color: "white"
            };
            const hasStuff =
                this.isSamePerson(person, workPermit.signedBy) ||
                this.isSamePerson(person, workPermit.surrenderedBy)
            if(hasStuff) {
                styles.borderTopLeftRadius = "5px";
                styles.borderTopRightRadius = "5px";
            }
            else {
                styles.borderRadius = "5px";
            }
            return styles;
        }

        onWorkPermitDelete(event: CustomEvent<any>) {
            const name = event.detail as string;
            toastr.info(name, "Deleted Work Permit");
        }

        userDescription(workPermit: WorkPermit): string {
            const user = this.userList.find(i => i.id === workPermit.createdByUserID);
            return user == null ? "- - -" : `${user.forename} ${user.surname}`;
        }

        clientDescription(workPermit: WorkPermit): string {
            const clientID = this.siteList.find(i => i.id === workPermit.siteID)?.clientID ?? "";
            return this.clientList.find(i => i.id === clientID)?.description ?? "- - -";
        }

        siteDescription(workPermit: WorkPermit): string {
            return this.siteList.find(i => i.id === workPermit.siteID)?.description ?? "- - -";
        }

        statusColour(workPermit: WorkPermit): string {
            const status = workPermit.latestStatus;
            if(status === WorkPermitEventType.Draft) return "#CCC";
            // Paul ordered these colours
            if(status === WorkPermitEventType.Issued) return "#1BA4DE";
            if(status === WorkPermitEventType.IssuedExpired) return "#E72275";
            if(status === WorkPermitEventType.Closed) return "#76B94E";
            if(status === WorkPermitEventType.Surrendered) return "#FF9232";
            return "#EEE";
        }

        statusIcon(workPermit: WorkPermit): string {
            const status = workPermit.latestStatus;
            if(status === WorkPermitEventType.Draft) return "mdi-file-edit-outline";
            if(status === WorkPermitEventType.Issued) return "mdi-check-decagram";
            if(status === WorkPermitEventType.IssuedExpired) return "mdi-check-decagram";
            if(status === WorkPermitEventType.Surrendered) return "mdi-rotate-left";
            if(status === WorkPermitEventType.Closed) return "mdi-progress-close";
            return "mdi-circle";
        }

        showAddNewDialogue(): void {
            const dialog: WorkPermitAddDialogue = this.$refs.workPermitAddDialogue as WorkPermitAddDialogue;
            dialog.show();
        }

        onAddedWorkPermit(workPermitId: string): void {
            this.$router.push({ name: "AdminWorkPermit", params: { workPermitID: workPermitId } });
        }

        edit(workPermit: IWorkPermit): void {
            this.$router.push({ name: "AdminWorkPermit", params: { workPermitID: workPermit.id } });
        }

        refreshSearch(): void {
            this.resultCount = "-";
            this.workPermitList = [];
            this.searchParameters.pageNumber = 1;
            this.infiniteId += 1;
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            const startTime = performance.now();
            const response: ISearchResponse = await ApiClient.post("/Api/workPermit/Search", this.searchParameters);
            const gotResponseTime = performance.now();
            console.log(`@@@ Loading results page took ${(gotResponseTime - startTime).toFixed(2)} ms`);
            if(this.searchParameters.pageNumber === 1) {
                this.resultCount = String(response.count);
            }
            if (response.list.length) {
                //this.workPermitList.push(...mapData<Array<WorkPermit>>(response.list, { root: (s) => new WorkPermit (s) }));
                this.workPermitList.push(...response.list.map((s: IWorkPermit) => new WorkPermit(s)));
                this.searchParameters.pageNumber += 1;
                stateChanger.loaded();
                const mappedTime = performance.now();
                console.log(`@@@ Mappping results took ${(mappedTime - gotResponseTime).toFixed(2)} ms`);
            }
            else {
                stateChanger.complete();
            }
        }
    }
